import AntdSelect from "antd/lib/select";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { SelectPlaceholder, SelectPrefix, SelectWrapper } from "./elements";
import "./index.sass";

const AUTH_SELECT_DROPDOWN_CLASS = "bt-auth-select-dropdown";

const Select = ({
	prefixIcon,
	selectPlaceholder,
	value,
	dropdownClassName,
	dropdownMatchSelectWidth = false,
	"data-testid": dataTestId,
	onChange,
	onFocus: onFocusProp,
	onBlur: onBlurProp,
	onDropdownVisibleChange: onDropdownVisibleChangeProp,
	...props
}) => {
	const [hasValue, setHasValue] = useState(!!value);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const wrapperRef = useRef(null);

	useEffect(() => {
		setHasValue(!!value);
	}, [value]);

	const applyTextTestId = useCallback(() => {
		const el = wrapperRef.current;
		if (!dataTestId || !el || !el.querySelector) return;
		const item = el.querySelector(".ant-select-selection-item");
		if (item) {
			item.setAttribute("data-testid", `${dataTestId}-text`);
		}
	}, [dataTestId]);

	useEffect(() => {
		const el = wrapperRef.current;
		if (!dataTestId || !el || !el.querySelector) return;
		applyTextTestId();
		const observer = new MutationObserver(applyTextTestId);
		observer.observe(el, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [dataTestId, applyTextTestId]);

	const handleFocus = (event) => {
		setIsFocused(true);
		event.target.setAttribute("autocomplete", "none");
		onFocusProp?.(event);
	};

	const handleBlur = (event) => {
		setIsFocused(false);
		onBlurProp?.(event);
	};

	const handleDropdownVisibleChange = (open) => {
		setIsDropdownOpen(open);
		onDropdownVisibleChangeProp?.(open);
	};

	const showCustomPlaceholder =
		!hasValue && !isDropdownOpen && !isFocused && Boolean(selectPlaceholder);

	const handleSelectChange = (nextValue, option) => {
		setHasValue(!!nextValue);
		if (option?.className) {
			const hasValueClass = nextValue ? "has-value" : "";
			option.className = `${option.className} ${hasValueClass}`;
		}
		onChange?.(nextValue, option);
	};

	return (
		<SelectWrapper innerRef={wrapperRef}>
			{prefixIcon && (
				<SelectPrefix className="prefix-icon-wrapper">{prefixIcon}</SelectPrefix>
			)}
			{showCustomPlaceholder && (
				<SelectPlaceholder className="select-placeholder">
					{selectPlaceholder}
				</SelectPlaceholder>
			)}
			<AntdSelect
				{...props}
				data-testid={dataTestId}
				dropdownClassName={[AUTH_SELECT_DROPDOWN_CLASS, dropdownClassName]
					.filter(Boolean)
					.join(" ")}
				dropdownMatchSelectWidth={dropdownMatchSelectWidth}
				dropdownStyle={{ minWidth: 280, maxWidth: 360 }}
				listHeight={220}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onDropdownVisibleChange={handleDropdownVisibleChange}
				onChange={handleSelectChange}
				value={value}
			/>
		</SelectWrapper>
	);
};

Select.Option = AntdSelect.Option;
export default Select;
